import { record } from 'fp-ts';
import { Row } from '@tanstack/react-table';
import { OnboardingAuditField, OnboardingAuditResult, OnboardingAuditStatus } from '@mews-commander/data-access';
import { AuditStatusFilter } from './routeSchemas';

export type AuditCheckResults = Omit<OnboardingAuditResult, 'legalEnvironmentData' | 'kycStatus'>;

export type AuditNames = keyof AuditCheckResults;

export const auditMatchesStatusFilter = ({ status }: OnboardingAuditField, filter: AuditStatusFilter): boolean => {
    switch (filter) {
        case AuditStatusFilter.All:
            return true;
        case AuditStatusFilter.Pending:
            return status !== OnboardingAuditStatus.Completed;
        case AuditStatusFilter.Completed:
            return status === OnboardingAuditStatus.Completed;
        case AuditStatusFilter.Failed:
            return status === OnboardingAuditStatus.NoGo;
    }
};

export const flattenAudits = (audits: AuditCheckResults) =>
    record.keys(audits).reduce((acc, name) => {
        acc.push(audits[name]);
        return acc;
    }, [] as Array<OnboardingAuditField>);

/** Group results from the API into their areas */
export const groupAudits = (audits: Record<AuditNames, OnboardingAuditField>): OnboardingAuditField[] => {
    const areaOrder: string[] = [];

    const grouped = record.keys(audits).reduce(
        (acc, name) => {
            const audit = audits[name];
            if (!audit) {
                return acc;
            }

            if (!(audit.area in acc)) {
                acc[audit.area] = [];
                areaOrder.push(audit.area);
            }
            acc[audit.area].push(audit);
            return acc;
        },
        {} as Record<string, OnboardingAuditField[]>
    );

    return areaOrder.flatMap((area) => grouped[area]);
};

/** Reduces a list of Audit into one status */
const calculateAggregateStatus = (statuses: OnboardingAuditStatus[]): OnboardingAuditStatus => {
    let status: OnboardingAuditStatus = OnboardingAuditStatus.Completed;

    statuses.forEach((auditStatus) => {
        if (status === OnboardingAuditStatus.NoGo || auditStatus === OnboardingAuditStatus.Completed) {
            return;
        }

        if (auditStatus === OnboardingAuditStatus.NoGo) {
            status = OnboardingAuditStatus.NoGo;
            return;
        }

        if (auditStatus === OnboardingAuditStatus.Incomplete) {
            if (status === OnboardingAuditStatus.Advice) {
                return;
            }
            status = OnboardingAuditStatus.Incomplete;
        }

        if (auditStatus === OnboardingAuditStatus.Advice) {
            status = OnboardingAuditStatus.Advice;
        }
    });

    return status;
};

export const collateStatusFromFields = (results: Row<OnboardingAuditField>): OnboardingAuditStatus => {
    if (!results.subRows) return OnboardingAuditStatus.Completed;

    const statuses = results.subRows.map((audit) => audit.original?.status).filter((status) => Boolean(status));

    return calculateAggregateStatus(statuses);
};

export const collateStatusFromFieldsArray = (fields: OnboardingAuditField[]): OnboardingAuditStatus => {
    if (!fields) {
        return OnboardingAuditStatus.Completed;
    }

    const statuses = fields.map((field) => field.status);
    return calculateAggregateStatus(statuses);
};
